import { useEffect, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router";
import { motion } from "motion/react";
import { Bot, KeyRound, LayoutGrid, PlugZap, Workflow } from "lucide-react";
import { permissionMatches, useAuth, type AuthResponse } from "@ncp/api-client";
import { StaffSignIn } from "@ncp/assistant-sdk";
import { useLocale, useTranslation } from "@ncp/i18n";
import { Button, InlineAlert, Stagger, fadeUp, getSafeRedirect } from "@ncp/ui";

function useLoginDestination(): string {
  const location = useLocation();
  const [params] = useSearchParams();
  const from = (location.state as { from?: unknown } | null)?.from ?? params.get("from");
  return getSafeRedirect(from, "/");
}

/** Identities that may enter the console: platform administrators or anyone whose roles map to a platform permission. */
function hasConsoleAccess(user: AuthResponse["user"]): boolean {
  if (user.kind !== "Staff") return false;
  if (user.permissions?.length) return user.permissions.some((p) => p === "*" || p.startsWith("platform."));
  return user.roles.includes("Admin") || permissionMatches(user.permissions, "platform.overview.read");
}

/**
 * Platform Console sign-in (security §14.1): password, then the authenticator code; a first sign-in replaces the temporary password and
 * sets up the authenticator. A session without console permissions is signed out again here with the reason.
 */
export function LoginPage() {
  const { t } = useTranslation();
  const { locale, toggle } = useLocale();
  const auth = useAuth();
  const navigate = useNavigate();
  const destination = useLoginDestination();
  const [refused, setRefused] = useState(false);

  const signedIn = auth.isAuthenticated && auth.user ? hasConsoleAccess(auth.user) : false;
  useEffect(() => {
    if (signedIn) navigate(destination, { replace: true });
  }, [signedIn, destination, navigate]);

  // A sign-in (password, Ministry account or handoff) without console permissions is ended again here, on the server too.
  useEffect(() => {
    if (!auth.isAuthenticated || !auth.user || hasConsoleAccess(auth.user)) return;
    setRefused(true);
    auth.clear();
  }, [auth]);

  const capabilities = [
    { icon: LayoutGrid, label: t("admin:login.capabilities.services") },
    { icon: Workflow, label: t("admin:login.capabilities.workflows") },
    { icon: Bot, label: t("admin:login.capabilities.agents") },
    { icon: PlugZap, label: t("admin:login.capabilities.integrations") },
  ];

  return (
    <div className="relative min-h-dvh overflow-hidden">
      <div aria-hidden className="pointer-events-none absolute start-[30%] top-[40%] size-[640px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-[radial-gradient(closest-side,rgb(182_138_53/0.12),transparent)] rtl:translate-x-1/2" />

      <div className="relative mx-auto flex min-h-dvh max-w-6xl flex-col px-6 py-6 sm:px-10">
        <motion.header className="flex items-center justify-between gap-4" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.4 }}>
          <img src={locale === "ar" ? `${import.meta.env.BASE_URL}brand/moet-logo-ar.png` : `${import.meta.env.BASE_URL}brand/moet-logo-en.png`} alt={t("app.ministry")} className="h-10 w-auto" />
          <Button variant="ghost" size="sm" onClick={toggle}>
            <span lang={locale === "ar" ? "en" : "ar"}>{locale === "ar" ? "English" : "العربية"}</span>
          </Button>
        </motion.header>

        <main className="grid flex-1 items-center gap-12 py-10 lg:grid-cols-[1.1fr_1fr]">
          <Stagger className="flex flex-col">
            <motion.p variants={fadeUp} className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-aegold-700 rtl:text-[13px] rtl:normal-case rtl:tracking-normal">
              <KeyRound className="size-3.5" /> {t("admin:app.title")}
            </motion.p>
            <motion.h1 variants={fadeUp} className="mt-3 text-3xl font-semibold text-aeblack-950 sm:text-4xl">
              {t("app.platform")}
            </motion.h1>
            <motion.p variants={fadeUp} className="mt-3 max-w-lg text-sm text-sand-600 sm:text-base">
              {t("admin:app.subtitle")}
            </motion.p>
            <motion.ul variants={fadeUp} className="mt-8 grid gap-3 sm:grid-cols-2">
              {capabilities.map((c) => (
                <li key={c.label} className="flex items-start gap-3 rounded-xl border border-sand-200 bg-white/70 p-3 text-sm">
                  <span className="grid size-8 shrink-0 place-items-center rounded-lg bg-aegold-50 text-aegold-800">
                    <c.icon className="size-4" />
                  </span>
                  <span className="pt-1">{c.label}</span>
                </li>
              ))}
            </motion.ul>
          </Stagger>

          <Stagger className="flex w-full flex-col items-center">
            <motion.div variants={fadeUp} className="w-full max-w-md">
              {refused ? <InlineAlert tone="danger" className="mb-4 text-start">{t("admin:login.noAccess")}</InlineAlert> : null}
              <StaffSignIn
                surface="admin"
                from={destination}
                onSignedIn={(s) => (hasConsoleAccess(s.user) ? navigate(destination, { replace: true }) : undefined)}
                heading={
                  <div>
                    <h2 className="text-lg font-semibold text-aeblack-950">{t("admin:login.title")}</h2>
                    <p className="mt-1 text-xs text-sand-600">{t("admin:login.subtitle")}</p>
                  </div>
                }
              />
            </motion.div>
            <motion.p variants={fadeUp} className="mt-4 max-w-sm text-center text-xs leading-5 text-sand-600">
              {t("admin:login.sessionNote")}
            </motion.p>
          </Stagger>
        </main>

        <motion.footer className="flex flex-col items-center gap-1 text-xs text-sand-600 sm:flex-row sm:justify-between" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.4, delay: 0.3 }}>
          <span>
            {t("app.ministry")} · {t("app.department")}
          </span>
          <span>{t("app.poweredBy")}</span>
        </motion.footer>
      </div>
    </div>
  );
}
